import { RawParams, StateOrName, TransitionOptions, TransitionService, UIRouterGlobals } from "@uirouter/angular";
import { Component } from "@angular/core";
import { Observable, takeUntil } from "rxjs";
import { HomeScreenView } from "@gtmhub/home/utils";
import { CurrentUserService } from "@gtmhub/users";
import { ICollection } from "@webapp/core/core.models";
import { fromTransitionHook } from "@webapp/core/routing/rxjs";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import {
  FAVORITES_STEP,
  INITIAL_STEP,
  NAVIGATION_COLOR_STEP,
  NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS,
  RECENTS_AND_SUGGESTED_STEP,
  SETTINGS_STEP,
  STILL_HAVE_QUESTIONS_STEP,
} from "@webapp/navigation/models/navigation-onboarding.models";
import { SubNavOption } from "@webapp/navigation/models/sub-nav-options.models";
import { NavigationOnboardingService } from "@webapp/navigation/services/navigation-onboarding.service";
import NavigationItemsMediator from "@webapp/navigation/services/uxcustomization/navigation-items.mediator.service";
import { getNavItemsType } from "@webapp/navigation/utils/nav-items.util";
import { IOnboardingDefinition, IOnboardingDelegate, IOnboardingJourneyState } from "@webapp/shared/components/onboarding/onboarding.models";
import { INavItem, NavItemsGroupType, NavItemsSectionBroadcastConfig, NavItemsType } from "../../../../models/nav-items-list.models";
import { NewNavigationEvents } from "../../events";
import { NavItemsSectionBroadcastConfigFactory } from "../services/nav-items-section-broadcast-config.factory";

@Component({
  selector: "home-sub-navigation",
  templateUrl: "./home-sub-navigation.component.html",
  providers: [DestroyService],
})
export class HomeSubNavigationComponent implements IOnboardingDelegate {
  public options: SubNavOption[] = [
    { label: "dashboard", icon: "table", state: "gtmhub.home.dashboard", broadcastMessage: NewNavigationEvents.SUB_NAV_SWITCH_TO_DASHBOARD },
    { label: "feed", icon: "parent-child-view", state: "gtmhub.home.feed", broadcastMessage: NewNavigationEvents.SUB_NAV_SWITCH_TO_FEED },
  ];
  public sessions$: Observable<ICollection<INavItem>>;
  public okrs$: Observable<ICollection<INavItem>>;
  public kpis$: Observable<ICollection<INavItem>>;
  public whiteboards$: Observable<ICollection<INavItem>>;
  public lists$: Observable<ICollection<INavItem>>;
  public insightboards$: Observable<ICollection<INavItem>>;
  public people$: Observable<ICollection<INavItem>>;
  public loading$: Observable<boolean>;
  public visibleItemsCount = 3;
  public indicators: { loading?: boolean } = {};
  public itemsGroupType = "favorites" as NavItemsGroupType;
  public broadcastConfig: NavItemsSectionBroadcastConfig = this.broadcastConfigFactory.buildDefaultNavItemsSectionBroadcastConfig();
  public homeHasNoFavorites = false;
  public sectionsWithItems: NavItemsType[];
  public onboarding: IOnboardingDefinition = { shouldShow: false, totalSteps: 0, previewOverrides: { totalSteps: NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS } };

  public constructor(
    protected navigationItemsMediator: NavigationItemsMediator,
    private currentUserService: CurrentUserService,
    private navigationOnboardingService: NavigationOnboardingService,
    private destroyService$: DestroyService,
    private broadcastConfigFactory: NavItemsSectionBroadcastConfigFactory,
    routerGlobals: UIRouterGlobals,
    transitionService: TransitionService
  ) {
    const SECTIONS_LIST: NavItemsType[] = ["sessions", "okrs", "kpis", "whiteboards", "lists", "insightboards", "people"];
    this.sectionsWithItems = SECTIONS_LIST;
    this.navigationItemsMediator.loadFavorites(SECTIONS_LIST);

    SECTIONS_LIST.forEach((navItemsType: NavItemsType) => {
      this[navItemsType + "$"] = this.navigationItemsMediator.getFavoritesItems$(navItemsType);
      this[navItemsType + "$"].subscribe((collection) => {
        if (!collection) return;
        this.collectionUpdated(navItemsType, collection.totalCount);
      });
    });

    fromTransitionHook(transitionService, "onSuccess", {})
      .pipe(takeUntil(this.destroyService$))
      .subscribe(() => {
        const state = routerGlobals.current.name;
        const activeScreen = state.split(".")[2];
        const homeScreenViewValueLabel = { [HomeScreenView.dashboard]: "dashboard", [HomeScreenView.feed]: "feed" };
        this.setActiveOption(homeScreenViewValueLabel[activeScreen]);
      });

    this.navigationOnboardingService
      .getState$()
      .pipe(takeUntil(this.destroyService$))
      .subscribe((state: IOnboardingJourneyState) => {
        this.onboarding = { shouldShow: false, totalSteps: 0, delegate: undefined, previewOverrides: { totalSteps: NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS } };
        if (state.stage === "PENDING_START" && state.currentStep === INITIAL_STEP) {
          this.onboarding.shouldShow = true;
          this.navigationOnboardingService.markJourneyAsShown();
        }
        if (state.stage === "ONGOING" && state.currentStep === FAVORITES_STEP) {
          this.onboarding.startingStep = FAVORITES_STEP;
          this.onboarding.shouldShow = true;
        }
        if (state.stage === "ONGOING" && state.currentStep === STILL_HAVE_QUESTIONS_STEP) {
          this.onboarding.startingStep = STILL_HAVE_QUESTIONS_STEP;
          this.onboarding.shouldShow = true;
          if (!state.isCompleted) {
            this.navigationOnboardingService.completeJourney();
          }
        }
        if (state.stage === "ENDED_BY_USER") {
          this.onboarding.shouldShow = false;
        }
        this.onboarding.totalSteps = NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS;
        this.onboarding.previewOverrides.totalSteps = state.totalSteps;
        if (state.skipSteps && state.skipSteps.includes(SETTINGS_STEP)) {
          this.onboarding.previewOverrides.stepsMap = {
            [NAVIGATION_COLOR_STEP]: SETTINGS_STEP,
            [STILL_HAVE_QUESTIONS_STEP]: NAVIGATION_COLOR_STEP,
          };
        }
        this.onboarding.delegate = this;
      });
  }

  public shouldSeeOnboarding(): boolean {
    return this.onboarding.shouldShow;
  }

  public getRedirectStateForStep(step: number): null | { to: StateOrName; params?: RawParams; options?: TransitionOptions } {
    if (step === RECENTS_AND_SUGGESTED_STEP) {
      this.navigationOnboardingService.moveForward({ inTransition: true });
      return { to: "gtmhub.sessions" };
    }
    if (step === NAVIGATION_COLOR_STEP) {
      this.navigationOnboardingService.moveBackward({ inTransition: true });
      return { to: "gtmhub.profileTheme" };
    }
  }

  private setActiveOption(activeOptionLabel: string): void {
    const labelHomeScreenViewValue = { dashboard: HomeScreenView.dashboard, feed: HomeScreenView.feed };
    this.options.forEach((option) => {
      if (option.label === activeOptionLabel) {
        option.isActive = true;
        this.currentUserService.setUserSetting({ homeScreen: labelHomeScreenViewValue[option.label] });
      } else {
        option.isActive = false;
      }
    });
  }

  private collectionUpdated(collectionName: NavItemsType, totalCount: number): void {
    if (totalCount === 0) {
      this.sectionsWithItems = this.sectionsWithItems.filter((item) => item !== collectionName);
    } else {
      this.sectionsWithItems.push(collectionName);
    }
    this.homeHasNoFavorites = this.sectionsWithItems.length === 0;
  }

  public onUnmarkItemAsFavorite(item: INavItem): void {
    const navItemsType: NavItemsType | undefined = getNavItemsType(item.uxcType);
    if (navItemsType !== undefined) {
      this.navigationItemsMediator.unmarkItemAsFavorite(item.favoriteId, item.uxcType, item, navItemsType);
    }
  }

  public journeyCompleted(): void {
    this.navigationOnboardingService.completeJourney();
  }

  public journeyClosed(): void {
    this.navigationOnboardingService.closeJourney();
  }

  public journeyStarted(): void {
    this.navigationOnboardingService.startJourney();
  }
}
